import type { ReadyResult } from '@/features/dashboard/types';
import type { ChipTone } from '@/components/common/StatusChip';

/** Readiness tones reported by the health widgets. */
export type StatusTone = 'up' | 'down' | 'degraded' | 'unknown';

/** Map a readiness tone to the shared StatusChip tone + default label. */
export const statusChip: Record<StatusTone, { tone: ChipTone; label: string }> = {
  up: { tone: 'success', label: 'Operational' },
  down: { tone: 'danger', label: 'Down' },
  degraded: { tone: 'warning', label: 'Degraded' },
  unknown: { tone: 'neutral', label: 'Unknown' },
};

export interface ServiceStatus { name: string; tone: StatusTone }

/** Flatten Terminus info/error/details into a single name->status list. */
export function servicesFromReady(ready: ReadyResult | undefined): ServiceStatus[] {
  if (!ready) return [];
  const merged: Record<string, { status: string }> = {
    ...(ready.details ?? {}),
    ...(ready.info ?? {}),
    ...(ready.error ?? {}),
  };
  return Object.entries(merged).map(([name, v]) => ({
    name,
    tone: v.status === 'up' ? 'up' : 'down',
  }));
}

/** Overall tone from the readiness payload. */
export function overallTone(ready: ReadyResult | undefined): StatusTone {
  if (!ready) return 'unknown';
  if (ready.status === 'ok') return 'up';
  const services = servicesFromReady(ready);
  const anyUp = services.some((s) => s.tone === 'up');
  return anyUp ? 'degraded' : 'down';
}
